import { useState } from 'react';
import { Box, Button, Container, Heading, Text, Input, Stack, Flex, HStack, Center } from '@chakra-ui/react';
import { Field } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import posthog from 'posthog-js';
import { Toaster, toaster } from "../ui/toaster"

// X (Twitter) Icon component
const XIcon = (props: any) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface FormData {
  email: string;
  name: string;
}

const MailinglistSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    // Track form submission event with debugging
    console.log('Submitting Mailinglist form, tracking event');
    try {
      posthog.capture('Mailinglist_submit', {
        name_provided: !!data.name.trim(),
        email_domain: data.email.split('@')[1],
        timestamp: new Date().toISOString()
      });
      console.log('Mailinglist submit event captured successfully');
    } catch (error) {
      console.error('Error capturing Mailinglist submit event:', error);
    }
    
    try {
      // Submit to local MailingListController endpoint
      const response = await fetch('/mailinglist/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email
        })
      });
      
      if (response.ok) {
        toaster.success({
          title: "Success!",
          description: "You've been added to our mailing list. We'll notify you updates!",
          duration: 5000,
          closable: true,
        });
        reset();
      } else if (response.status === 409) {
        toaster.error({
          title: "Already Subscribed",
          description: "This email is already on our mailing list.",
          duration: 5000,
          closable: true,
        });
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Mailing list signup error:', error);
      toaster.error({
        title: "Error",
        description: "There was an error adding you to the mailing list. Please try again.",
        duration: 5000,
        closable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box id="mailinglist" py={20} bg="brand.light">
      <Toaster />
      <Container maxW="1200px">
        <Flex direction={{ base: 'column', md: 'row' }} align="center" justify="space-between">
          <Box flex={1} mb={{ base: 10, md: 0 }} mr={{ md: 10 }}>
            <Heading
              as="h2"
              textStyle="heading"
              mb={5}
            >
              Stay Updated
            </Heading>
            <Text textStyle="body" color="gray.600" mb={6}>
              Join our Mailinglist to be notified of the latest updates. Making use of the latest features to save you time and money on household maintenance, home repairs and DIY projects.
            </Text>
            <HStack gap={4} mb={6}>
              <Flex align="center" justify="center" w={12} h={12} rounded="full" bg="brand.primary" color="gray.800" fontSize="xl" fontWeight="bold">1</Flex>
              <Text textStyle="body">Sign up with your email</Text>
            </HStack>
            <HStack gap={4} mb={6}>
              <Flex align="center" justify="center" w={12} h={12} rounded="full" bg="brand.primary" color="gray.800" fontSize="xl" fontWeight="bold">2</Flex>
              <Text textStyle="body">Get notified about the latest updates</Text>
            </HStack>
          </Box>
          
          <Box
            flex={1}
            p={8}
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            maxW={{ base: "100%", md: "450px" }}
          >
               <Heading as="h2" textStyle="heading" mb={6} textAlign="center">Stay Updated</Heading>
            <Heading as="h3" textStyle="subheading" mb={6} textAlign="center">Join the Mailing List</Heading>
            
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={6}>
                <Field.Root invalid={!!errors.name}>
                  <Field.Label>Name</Field.Label>
                  <Input
                    id="name"
                    placeholder="Your name"
                    {...register("name", { 
                      required: "Name is required",
                    })}
                  />
                  <Field.ErrorText>{errors.name?.message}</Field.ErrorText>
                </Field.Root>
                
                <Field.Root invalid={!!errors.email}>
                  <Field.Label>Email</Field.Label>
                  <Input
                    id="email"
                    placeholder="your.email@example.com"
                    {...register("email", { 
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address"
                      }
                    })}
                  />
                  <Field.ErrorText>{errors.email?.message}</Field.ErrorText>
                </Field.Root>
                
                <Button
                  type="submit"
                  size="lg"
                  bg="brand.secondary"
                  color="white"
                  loading={isSubmitting}
                  _hover={{ bg: 'brand.dark' }}
                  width="100%"
                  onClick={() => {
                    console.log('Mailinglist button click');
                    try {
                      posthog.capture('Mailinglist_submit_button_click', { timestamp: new Date().toISOString() });
                      console.log('Mailinglist button click event captured');
                    } catch (error) {
                      console.error('Error capturing Mailinglist button click:', error);
                    }
                  }}
                >
                  Join Mailing List
                </Button>
                
                <Box mt={4}>
                  <Text textAlign="center" fontSize="sm" mb={2}>Or follow us on</Text>
                  <Center>
                    <Button asChild
                      bg="black"
                      color="white"
                      _hover={{ bg: 'gray.800' }}
                      size="md"
                      borderRadius="full"
                      onClick={() => {
                        console.log('Social click (X/Twitter)');
                        try {
                          posthog.capture('social_click', { 
                            platform: 'x', 
                            handle: '@diybuddy_app',
                            timestamp: new Date().toISOString() 
                          });
                          console.log('Social click event captured');
                        } catch (error) {
                          console.error('Error capturing social click:', error);
                        }
                      }}
                    >
                      <a href="https://x.com/diybuddy_app" target="_blank" rel="noopener noreferrer">
                        <HStack gap={2}>
                          <XIcon fontSize="1.2em" />
                          <span>@diybuddy_app</span>
                        </HStack>
                      </a>
                    </Button>
                  </Center>
                </Box>
              </Stack>
            </form>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default MailinglistSection;
